import { Subscription } from "../../../bt-common/schemas/bt/subscription";
import { User, UserInterface } from "@common-models/user";
import { loadLabelsAndContactLabelsThunk } from "@common-reducers/labels-reducer/labels-thunks/crud";
import { MODAL, modalActions } from "@common-reducers/ModalReducer";
import { userSlice } from "@common-reducers/UserReducer";
import { setAuthenticationStatus, setAuthFailure } from "@common-reducers/UserReducer";
import { classifyAuthFailure } from "@/common/bt-auth/auth-failure";
import {
  clearStateThunk,
  loadUserMessagesAndCampaignsThunk,
  updateUserPhoneNumberThunk,
  validateSubscriptionsThunk
} from "@common-reducers/UserThunks";
import { getWorkspaceDataThunk } from "@common-reducers/WorkspaceThunks";
import { isExtensionContext, isExtensionContextOrWhatsappView, isWebPageButNotWhatsappPage } from "@common-services/detect-context";
import { setUserSegment } from "@common-services/analytics-config";
import { authService } from "@/common/bt-auth/auth-service";
import { setDarkModeClasses } from "@common-services/utils";
import { consumeOpenCrispIntentWithRetry } from "@common-services/crisp-after-login";
import { activeSubscriptionSelector, selectCurrentWorkspace } from "@common-reducers/UserSelectors";
import { shouldCaptureTimezone, getBrowserTimezone } from "@common/utils/timezone-capture";
import { connectNotifier } from "@common/noifier-module/NotifierThunks";
import { setUserAuthenticated } from "@common-reducers/ConnectionReadinessSlice";
import { initAppData, AuthParams, backendApi } from "./backend-api";
import { loadWaEventGateDataThunk } from "@extension-context-content/thunks/WaEventDispatchThunk";
import { loadLinkedChats, startLinkedChatsAutoRefresh } from "@extension-context-content/services/ai-linked-chats-registry";
import { AnalyticsService } from "@common-services/analytics-service";
import { TeamInboxActions } from "../../../components/team-inbox/redux/TeamInboxReducer";
import { initializeUserLanguage } from "@common-services/language-detection";
import { applyDocumentDirection } from "@common-services/rtl";
import type { AppThunkDispatch, CommonRootState } from "@common/types/common-root-state-type";
let runningAuthProcesses = new Set<string>();

// Workspace ids we've already attempted a browser-timezone capture for in this
// session. Guards against spamming the backend when the post-auth bootstrap runs
// repeatedly (parallel/duplicate auth calls, account switches) — at most one
// capture attempt per workspace per session. The backend is still the
// authoritative SET-IF-EMPTY guard; this just prevents useless repeat calls.
const timezoneCaptureAttemptedWorkspaces = new Set<string>();

/**
 * Handle post-authentication setup
 */
// The full MutationLifecycleApi type from RTK Query is complex; we only need the
// fields actually used inside this handler. Picking them via an intersection that
// also accepts the wider RTK type keeps the callsite (backend-api.ts) compatible.
interface AuthLifecycleSubset {
  dispatch: AppThunkDispatch;
  queryFulfilled: Promise<{ data: { user: UserInterface; accessToken?: string; authentication?: { strategy?: string; payload?: { impersonating?: unknown } } } }> | Promise<{ data: unknown }>;
  getState: () => CommonRootState | unknown;
}
export const authenticateOnQueryStarted = async (
  id: AuthParams,
  api: AuthLifecycleSubset
) => {
  const dispatch = api.dispatch;
  const queryFulfilled = api.queryFulfilled as Promise<{ data: { user: UserInterface; accessToken?: string; authentication?: { strategy?: string; payload?: { impersonating?: unknown } } } }>;
  const getState = api.getState as () => CommonRootState;
  // Use a stable concurrency key to avoid duplicate side-effects when multiple
  // authenticate calls of the same strategy (e.g., JWT) are triggered in parallel
  const strategy = (id as any)?.strategy || 'unknown';
  const authKey = `auth:${strategy}`;
  try { console.log('[login-flow] authenticateOnQueryStarted: started', { id }); } catch {}
  
  // Dynamically import DBServiceThunks (used by the heavy per-user load below) — avoids a
  // circular dependency at module load.
  const {
    DBBoardsThunks,
    DBSessionThunks,
    DBSubscriptionThunks,
    DBUserThunks,
    DBCustomPromptsThunks,
    DBTemplateThunks,
    TEMPLATE_LIST_QUERY,
    UserReducerActions,
  } = await import(/* webpackMode: "eager" */ "@common-reducers/DBServiceThunks");

  console.log('[login-flow] authenticateOnQueryStarted: awaiting queryFulfilled');
  let result: any;
  try {
    result = await queryFulfilled;
    console.log('[login-flow] authenticateOnQueryStarted: queryFulfilled resolved');
  } catch (error: any) {
    try {
      console.log('[login-flow] authenticateOnQueryStarted: queryFulfilled rejected', {
        status: error?.error?.status ?? error?.status,
        data: error?.error?.data ?? error?.data,
        message: error?.error?.data?.message || error?.data?.message || error?.message
      });
    } catch {}
    // Settle to a terminal failure status that AGREES with the caller's own catch (jwt →
    // 'autoJwtFail' matching attemptJwtAuth; local/google → 'idle' matching login/googleLogin),
    // so the two writers never diverge for the same request. For jwt the REASON is carried
    // alongside the status (401/403 = the session really is over; network/timeout/5xx = a
    // backend blip that must NOT log the user out) — same classification attemptJwtAuth's own
    // catch applies, so whichever writer lands first, the guard sees the same verdict.
    try {
      if (strategy === 'jwt') {
        dispatch(setAuthFailure(classifyAuthFailure(error)));
      } else {
        dispatch(setAuthenticationStatus('idle'));
      }
    } catch {}
    return;
  }

  const user: UserInterface = result.data.user;
  console.log('[login-flow] authenticateOnQueryStarted: query fulfilled', { userId: user?._id, email: user?.email });

  // A JWT re-auth returns the user but usually NO fresh accessToken; fall back to the token we
  // authenticated with — but ONLY for jwt, so we never persist a google OAuth / local credential
  // as the app JWT (for local/google the response always carries the freshly-minted app JWT).
  const effectiveToken = (result as any)?.data?.accessToken ?? (strategy === 'jwt' ? (id as any)?.accessToken : undefined);

  // SINGLE-OWNER lightweight identity — ALWAYS runs on a successful fulfillment so route guards
  // and the popup settle, even when the heavy per-user load below is deduped away by a concurrent
  // same-strategy auth. Cheap + idempotent: persist token, set credentials, settle terminal status.
  authService.persistToken(effectiveToken);
  dispatch(userSlice.actions.setUserAndCredentials({ user, accessToken: effectiveToken }));
  try {
    const resolvedStrategy = (result as any)?.data?.authentication?.strategy ?? strategy;
    dispatch(setAuthenticationStatus((resolvedStrategy === 'jwt' ? 'autoJwtSuccess' : 'userLoginSuccess') as any));
  } catch {}
  console.log('[login-flow] authenticateOnQueryStarted: credentials + status settled');

  // Pop the support chat if the user was sent here from the side-panel
  // Support row (carries `?openCrisp=1`). This runs on the ALWAYS-RUNS
  // branch — before the heavy-load dedup short-circuits a concurrent
  // auth — so the intent survives both jwt auto-login and the manual
  // login flow. No-op if there's no pending flag in sessionStorage.
  consumeOpenCrispIntentWithRetry();

  // Heavy, side-effectful per-user load (clear state, upsert, data fetches). Dedup so concurrent
  // same-strategy authentications don't double-run it.
  if (runningAuthProcesses.has(authKey)) return;
  runningAuthProcesses.add(authKey);
  try {

    // Set up Microsoft Clarity tracking
    const clarityFn = (window as { clarity?: (...args: unknown[]) => void }).clarity;
    if (clarityFn) {
      clarityFn("identify", user.email);
    }

    // Clear existing state and set up user data
    console.log('[login-flow] authenticateOnQueryStarted: clearing DB state before upsert');
    await dispatch(clearStateThunk());
    console.log('[login-flow] authenticateOnQueryStarted: DB state cleared');
    
    const upserted = {
      id: result.data.user._id,
      ...result.data.user,
      impersonating: result.data?.authentication?.payload?.impersonating,
    } as any;
    dispatch(UserReducerActions.upsertOne(upserted));
    console.log('[login-flow] authenticateOnQueryStarted: upserted user in DBUserReducer', { id: upserted?._id });

    // Set theme and direction - Tailwind expects 'dark' class on html element
    // Uses shared function to ensure consistency across startup and toggle
    if (!isExtensionContext()) {
      setDarkModeClasses(result.data.user.isDarkMode);
    }

    let storedLanguage = 'en';
    try {
      const localLanguage = localStorage.getItem('i18nextLng');
      if (localLanguage) {
        storedLanguage = localLanguage;
      }
    } catch {
      /* localStorage may be unavailable */
    }

    const languageForDirection = result.data.user.preferredLanguage || storedLanguage;
    const initialDirection = applyDocumentDirection(languageForDirection);
    dispatch(userSlice.actions.setUserDirection(initialDirection));

    // Mark user as authenticated and initialize connection. Use effectiveToken (not
    // result.data.accessToken, which is undefined on a jwt re-auth) so the notifier socket
    // always connects with a valid token.
    dispatch(setUserAuthenticated(true));
    dispatch(connectNotifier({ accessToken: effectiveToken }));

    // Handle email confirmation (web only)
    if (isWebPageButNotWhatsappPage()) {
      const emailConfirmationToken = new URL(
        (window as any).location.href
      ).searchParams.get("emailConfirm");

      if (
        emailConfirmationToken &&
        emailConfirmationToken === user?.emailConfirmationToken
      ) {
        dispatch(
          DBUserThunks.patch({
            entity: { _id: user._id, isEmailConfirmed: true, emailConfirmationToken: undefined },
          })
        );
        dispatch(modalActions.setComponentToRender(MODAL.EMAIL_CONFIRMATION_THANKS));
      }
    }

    // Load subscription data
    const subscriptionListRes = await dispatch(DBSubscriptionThunks.find({}));
    const subscriptionList: Subscription[] = subscriptionListRes.payload;
    dispatch(validateSubscriptionsThunk(subscriptionList));

    // Set up analytics
    const analyticsService = new AnalyticsService();
    analyticsService.setAuthenticatedUser(user, subscriptionList);

    // Set user segment
    const state = getState();
    if (state) {
      const activeSubscription = activeSubscriptionSelector(state);
      const planType = activeSubscription?.planType?.toLowerCase() || 'free';

      setUserSegment({
        type: 'authenticated_user',
        source: isExtensionContext() ? 'extension' : 
                /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ? 'mobile' : 'web_app',
        plan_type: planType as 'free' | 'basic' | 'standard' | 'pro'
      });
    }

    // Load workspace data
    await dispatch(
      getWorkspaceDataThunk({
        workspaceId: user?.currentWorkspace?.toString() ?? '',
      })
    );

    // Capture the browser's IANA timezone onto the current workspace, SET-IF-EMPTY
    // only (ships with/before the BE#648 DST release so more existing series become
    // migratable). This is the shared post-auth bootstrap, so it runs on BOTH the
    // web app and the extension. Fire-and-forget: it must NEVER block or throw into
    // the auth pipeline. The pure `shouldCaptureTimezone` decides (empty workspace
    // tz AND a valid canonical IANA zone from the browser); the backend is the
    // authoritative set-if-empty + IANA guard. Deduped to one attempt per workspace
    // per session so repeated/parallel auth events can't spam it.
    try {
      const workspace = selectCurrentWorkspace(getState() as CommonRootState);
      const workspaceId = user?.currentWorkspace?.toString() ?? '';
      if (workspaceId && !timezoneCaptureAttemptedWorkspaces.has(workspaceId)) {
        const tzToCapture = shouldCaptureTimezone(workspace?.timezone, getBrowserTimezone());
        if (tzToCapture) {
          timezoneCaptureAttemptedWorkspaces.add(workspaceId);
          void dispatch(
            backendApi.endpoints.captureWorkspaceTimezone.initiate({ timezone: tzToCapture }),
          );
        }
      }
    } catch (error) {
      console.warn('[login-flow] authenticateOnQueryStarted: timezone capture skipped', error);
    }

    // Load session data
    dispatch(DBSessionThunks.find({}));

    // Load custom prompts
    dispatch(DBCustomPromptsThunks.find({ userId: user._id }));

    // Load message templates (campaigns core data). Must live here, in the
    // post-auth pipeline that runs AFTER clearStateThunk() — a fetch fired from
    // a leaf component's mount (e.g. the Templates tab) races the auth clear and
    // gets wiped, so on a cold reload templates would render empty. Boards
    // survive because getWorkspaceDataThunk reloads them; templates need this.
    dispatch(DBTemplateThunks.find({ deleted: false, ...TEMPLATE_LIST_QUERY }));
    
    // Do not toggle authenticating here. Reducers handle it centrally.

    // Load extension-specific data

    if (isExtensionContextOrWhatsappView()) {
      // Labels + contact-labels (the WA top-header labels row). Like the
      // templates fetch above, this MUST live in the post-auth pipeline, after
      // clearStateThunk(). top-header-main's useEffect fires its own load the
      // moment `userIsLoggedIn` flips — which is `setUserAndCredentials` a few
      // lines above — so on a login into an already-open WhatsApp tab that fetch
      // lands in the window that clearStateThunk() then wipes, leaving the row
      // empty (and `itemsLoaded` false) until a page refresh. Reloading here is
      // the only path that runs after the clear.
      dispatch(loadLabelsAndContactLabelsThunk());
      dispatch(loadUserMessagesAndCampaignsThunk());
      dispatch(updateUserPhoneNumberThunk());
      dispatch(DBBoardsThunks.find({ isWABoard: true }));
      dispatch(initAppData());
      // WA event gate data (all webhooks + ACTIVE bots, $limit: -1) must be
      // in the store BEFORE the first WhatsApp event, or the gate evaluates
      // against an empty list and drops it. Kept live via MQTT afterwards.
      dispatch(loadWaEventGateDataThunk());
      // btgpt "AI Conversation" linked-chat set — hydrate the content-script
      // registry so fromMe messages in a linked group open the bridge lane.
      // Best-effort + backend re-check is authoritative (design §2). Auto-
      // refresh (5 min + tab-focus) picks up groups created OUTSIDE this
      // instance — e.g. via the ai_conversation agent tool from the phone.
      void loadLinkedChats();
      startLinkedChatsAutoRefresh();
    }
    
    // Initialize user language preference (works for both extension and website)
    try {
      await initializeUserLanguage(user, dispatch);
      console.log('[login-flow] authenticateOnQueryStarted: language initialized');
    } catch (error) {
      console.warn('[login-flow] authenticateOnQueryStarted: failed to initialize language', error);
    }
    
  } finally {
    runningAuthProcesses.delete(authKey);
    console.log('[login-flow] authenticateOnQueryStarted: finished', { id });
  }
};

/**
 * Handle WhatsApp communication requests
 */
// Dynamic-shape WA-comm response — the dispatched cmd determines which fields
// are present, so we type queryFulfilled.data via a permissive index signature and
// rely on each switch-case branch to assert the local shape. Tightening to a tagged
// union is a follow-up tracked outside slice 121d-B.
interface WaCommResultData {
  type?: string;
  [key: string]: unknown;
}
export const waCommRequestOnQueryStarted = async (id: { chatId?: string; [key: string]: unknown }, { dispatch, queryFulfilled }: {
  dispatch: AppThunkDispatch;
  queryFulfilled: Promise<{ data: WaCommResultData }>;
}) => {
  const { WaContactsCacheReducerActions, TeamInboxChatsCacheReducerActions, TeamInboxMessagesCacheReducerActions } = await import(/* webpackMode: "eager" */ "@common-reducers/DBServiceThunks");
  const { clearTempChatDataIfChatAvailableThunk } = await import(/* webpackMode: "eager" */ "@/components/team-inbox/redux/TeamInboxThunks");

  let result;
  try {
    result = await queryFulfilled;
  } catch (error) {
    // Request failed - reset loading states to prevent UI from being stuck
    console.error('[waCommRequestOnQueryStarted] Request failed:', error);
    dispatch(TeamInboxChatsCacheReducerActions.setItemsLoading(false));
    dispatch(TeamInboxMessagesCacheReducerActions.setItemsLoading(false));
    return;
  }

  const type = result?.data?.type;

  switch (type) {

    case "requestPaginatedMessages": {
      const messagesArray = (result?.data?.getPaginatedMessagesRes ?? []) as unknown[];
      const hasMoreMessagesToLoad = (result?.data?.hasMoreMessagesToLoad ?? false) as boolean;
      const requestedChatId = id?.chatId;

      // Updated to use the new TeamInboxMessagesCache with single-chat approach
      if (messagesArray.length > 0) {
        // Transform messages to cache format
        // Generate a unique _id from available fields when id._serialized is missing
        type RawCacheMessage = Record<string, unknown> & {
          waMessageKey?: { _serialized?: string; id?: string };
          id?: { _serialized?: string };
          key?: { _serialized?: string; id?: string };
          fromMe?: boolean;
          chatId?: string;
          to?: string;
          timestamp?: number;
        };
        const messagesForCache = (messagesArray as RawCacheMessage[]).map((message, index) => {
          // Extract the actual WhatsApp message ID (needed for media fetching)
          // waMessageKey contains the original full message.id object from WhatsApp
          // id._serialized might be undefined if createLeanMessage couldn't extract it
          const actualWaMessageId = message.waMessageKey?._serialized || message.waMessageKey?.id ||
                                    message.id?._serialized || message.key?._serialized || message.key?.id;

          // For cache key, use the actual WhatsApp ID if available, otherwise generate one
          let cacheId = actualWaMessageId;

          // If id._serialized is missing, generate a fallback ID
          if (!cacheId) {
            // Use combination of fromMe, timestamp, and index to create unique ID
            const fromMePrefix = message.fromMe ? 'true' : 'false';
            const msgChatId = message.chatId || message.to || requestedChatId || 'unknown';
            cacheId = `${fromMePrefix}_${msgChatId}_${message.timestamp}_${index}`;
          }

          return {
            ...message,
            _id: cacheId,
            // Store the chatId from the request to enable proper filtering
            chatId: message.chatId || requestedChatId,
            // Also store the id object with _serialized for rendering
            id: message.id?._serialized ? message.id : { _serialized: cacheId },
            // Preserve the actual WhatsApp message ID for media fetching
            waMessageId: actualWaMessageId,
            // Mark as coming from WhatsApp
            source: 'whatsapp' as const
          };
        });

        dispatch(TeamInboxMessagesCacheReducerActions.upsertMany(messagesForCache));
      }

      dispatch(TeamInboxActions.setHasMoreMsgsToLoad(hasMoreMessagesToLoad));

      dispatch(TeamInboxMessagesCacheReducerActions.setItemsLoading(false));
      dispatch(TeamInboxMessagesCacheReducerActions.setItemsLoadedLocaly(true));

      break;
    }
    case "requestMessageMedia": {
      const mediaObject = (result?.data?.getMessageMediaRes ?? null) as { mediaUrl?: string; fileName?: string; fileSize?: number; fileType?: string } | null;
      const msgId = (result?.data?.msgId ?? null) as string | null;

      // console.log("[debug]-requestMessageMedia", mediaObject, "msgId", msgId);
      if (mediaObject?.mediaUrl && msgId) {
        dispatch(TeamInboxMessagesCacheReducerActions.updateOne({
          id: msgId,
          changes: {
            body: mediaObject.mediaUrl,
            filename: mediaObject.fileName,
            filesize: mediaObject.fileSize,
            mimetype: mediaObject.fileType,
            mediaStatus: 'loaded'
          }
        }));
      } else if (msgId) {
        dispatch(TeamInboxMessagesCacheReducerActions.updateOne({
          id: msgId,
          changes: {
            mediaStatus: 'error'
          }
        }));
      }
      break;
    }
    case "requestChatById": {
      const chat = result?.data?.getChatByIdRes ?? null;

      // Updated to use the new TeamInboxChatsCache
      if (chat) {
        const typedChat = chat as { _serialized: string; [key: string]: unknown };
        const chatForCache = {
          ...typedChat,
          _id: typedChat._serialized // Use _serialized as the _id for cache
        };
        dispatch(TeamInboxChatsCacheReducerActions.upsertOne(chatForCache));

        // Clear tempChatData if this is the current chat
        dispatch(clearTempChatDataIfChatAvailableThunk());
      }
      break;
    }
    case "requestContactsAndChatsBySearch": {
      const searchRes = result?.data?.getContactsAndChatsBySearchRes as { data?: { contacts?: unknown[]; chats?: Array<{ _serialized: string; [key: string]: unknown }> } } | undefined;
      const contacts = searchRes?.data?.contacts ?? [];
      const chats = searchRes?.data?.chats ?? [];


      if (chats.length > 0) {
        // Map the chat id to _id for the cache system (chat `id` is the
        // canonical JID string — the redundant `_serialized` alias was removed).
        const chatsForCache = chats.map((chat) => ({
          ...chat,
          _id: chat.id
        }));

        dispatch(TeamInboxChatsCacheReducerActions.setAll(chatsForCache));

        // Clear tempChatData if the current chat is now available in cache
        dispatch(clearTempChatDataIfChatAvailableThunk());
      }

      dispatch(TeamInboxActions.setFilteredChatAndContactList({ contacts, searchResponseStatus: 'received' }));

      // Set loading states for search
      dispatch(TeamInboxChatsCacheReducerActions.setItemsLoading(false));
      dispatch(TeamInboxChatsCacheReducerActions.setItemsLoadedLocaly(true));
      break;
    }
    case "requestWaMeInfo": {
      const waMeInfo = (result?.data?.getWaMeInfoRes ?? null) as { wid?: { _serialized?: string } } | null;
      
      // Store the WhatsApp session phone number in the TeamInbox reducer
      dispatch(TeamInboxActions.setWaSessionPhoneNumber(waMeInfo?.wid?._serialized ?? null));
      break;
    }
  }
}
